/**
 * CSV Import — browser-based CSV flight data import with header-driven field mapping.
 * Reads the first row as column headers and maps by name (case-insensitive).
 * Reuses pure functions from scripts/migrate-excel.js for field transformations.
 * Dedup-checks via shared flight-dedup.js to skip already-imported rows.
 *
 * Requirements: 17.1, 17.2, 17.3, 17.4, 17.5, 17.6, 17.7, 17.8, 17.9, 18.2, 18.3, 19.3, 19.4
 */

import {
  parseTimeToMins,
  mapFuelLevel,
  normalisePilotName,
} from '../../utils/excel-helpers.js';
import { buildDedupKey, findExistingFlights } from '../../utils/flight-dedup.js';
import { getAircraft, getPilots, createFlight, createFuelOil } from '../../services/api.js';
import { resolveImportPilotId } from '../../utils/alias-resolution.js';

// ── Constants ───────────────────────────────────────────────────────────────

/**
 * All 24 recognised CSV column names and their metadata.
 */
const RECOGNISED_COLUMNS = [
  { name: 'flight_date',              required: true,  description: 'Date of flight (YYYY-MM-DD)' },
  { name: 'aircraft',                 required: true,  description: 'Aircraft name or registration' },
  { name: 'pilot',                    required: true,  description: 'Pilot name' },
  { name: 'departure',                required: false, description: 'Departure airfield' },
  { name: 'destination',              required: false, description: 'Destination airfield' },
  { name: 'block_time_mins',          required: true,  description: 'Block time (h:mm or decimal hours)' },
  { name: 'flight_time_mins',         required: false, description: 'Flight time (h:mm or decimal hours)' },
  { name: 'main_tank_level',          required: false, description: 'Fuel level: Full, 3/4, 2/3, 1/2, 1/3, 1/4, Empty' },
  { name: 'right_wing_level',         required: false, description: 'Right wing fuel level' },
  { name: 'belly_tank_level',         required: false, description: 'Belly tank fuel level' },
  { name: 'left_wing_level',          required: false, description: 'Left wing fuel level' },
  { name: 'notes',                    required: false, description: 'Free text notes' },
  { name: 'fuel_uplift',              required: false, description: 'Fuel uplift (e.g. "50L @ £1.80")' },
  { name: 'fuel_account_type',        required: false, description: 'ACCOUNT, OVERSEAS_ACCOUNT, or NON_ACCOUNT' },
  { name: 'oil_level',               required: false, description: 'Oil level (numeric)' },
  { name: 'departure_time',           required: false, description: 'Actual departure time (HH:MM)' },
  { name: 'arrival_time',             required: false, description: 'Actual arrival time (HH:MM)' },
  { name: 'ifr_time_mins',            required: false, description: 'IFR time (h:mm or decimal hours)' },
  { name: 'takeoffs',                 required: false, description: 'Number of takeoffs (integer)' },
  { name: 'landings',                 required: false, description: 'Number of landings (integer)' },
  { name: 'who_pays',                 required: false, description: 'CAPTAIN or P2' },
  { name: 'p2_name',                  required: false, description: 'P2 pilot name' },
  { name: 'purpose',                  required: false, description: 'Flight purpose (e.g. Training)' },
  { name: 'fuel_uplift_cost_currency', required: false, description: 'Currency code (e.g. GBP)' },
];

const RECOGNISED_NAMES = new Set(RECOGNISED_COLUMNS.map((c) => c.name));
const REQUIRED_COLUMNS = RECOGNISED_COLUMNS.filter((c) => c.required).map((c) => c.name);

// ── Pure helpers (exported for testing) ─────────────────────────────────────

/**
 * Normalise a CSV header name: strip trailing asterisk, trim whitespace, lowercase.
 * @param {string} name - Raw header name from CSV
 * @returns {string} Normalised header name
 */
export function normaliseHeaderName(name) {
  if (name == null) return '';
  return String(name).replace(/\*+$/, '').trim().toLowerCase();
}

/**
 * Parse a CSV header line and map recognised columns to their field positions.
 *
 * @param {string} headerLine - The raw first line of the CSV file
 * @returns {{ mappedFields: Object, unmappedColumns: string[], missingRequired: string[] }}
 *   - mappedFields: { normalisedName → columnIndex }
 *   - unmappedColumns: header names that were not recognised
 *   - missingRequired: required column names that are missing
 */
export function parseCSVHeaders(headerLine) {
  const rawHeaders = splitCSVLine(headerLine);
  const mappedFields = {};
  const unmappedColumns = [];

  for (let i = 0; i < rawHeaders.length; i++) {
    const normalised = normaliseHeaderName(rawHeaders[i]);
    if (normalised === '') continue;

    if (RECOGNISED_NAMES.has(normalised)) {
      mappedFields[normalised] = i;
    } else {
      unmappedColumns.push(rawHeaders[i].trim());
    }
  }

  const missingRequired = REQUIRED_COLUMNS.filter((col) => !(col in mappedFields));

  return { mappedFields, unmappedColumns, missingRequired };
}

/**
 * Validate that a set of CSV headers contains all required columns.
 *
 * @param {string[]} headers - Array of raw header strings
 * @returns {{ valid: boolean, missingRequired: string[] }}
 */
export function validateCSVHeaders(headers) {
  const normalisedSet = new Set(
    (headers || []).map((h) => normaliseHeaderName(h))
  );

  const missingRequired = REQUIRED_COLUMNS.filter((col) => !normalisedSet.has(col));

  return {
    valid: missingRequired.length === 0,
    missingRequired,
  };
}

/**
 * Build the CSV template: header line with asterisk-suffixed required columns,
 * plus one example data row.
 *
 * @returns {{ headerLine: string, exampleLine: string }}
 */
export function buildCSVTemplate() {
  const headers = RECOGNISED_COLUMNS.map((col) =>
    col.required ? `${col.name}*` : col.name
  );

  const exampleValues = {
    'flight_date*': '2024-01-15',
    'aircraft*': 'G-ABCD',
    'pilot*': 'John Smith',
    departure: 'EGLL',
    destination: 'EGBB',
    'block_time_mins*': '90',
    flight_time_mins: '75',
    main_tank_level: 'Full',
    right_wing_level: '3/4',
    belly_tank_level: '1/2',
    left_wing_level: '3/4',
    notes: 'Training flight',
    fuel_uplift: '50L @ £1.80',
    fuel_account_type: 'ACCOUNT',
    oil_level: '6',
    departure_time: '09:15',
    arrival_time: '10:30',
    ifr_time_mins: '0:25',
    takeoffs: '1',
    landings: '1',
    who_pays: 'CAPTAIN',
    p2_name: 'Jane Doe',
    purpose: 'Training',
    fuel_uplift_cost_currency: 'GBP',
  };

  const exampleRow = headers.map((h) => exampleValues[h] || '');

  return {
    headerLine: headers.join(','),
    exampleLine: exampleRow.join(','),
  };
}

/**
 * Return column metadata for the CSV import template.
 * Used by both the downloadable template and the on-page column reference.
 * @returns {{ name: string, required: boolean, description: string }[]}
 */
export function getColumnReference() {
  return RECOGNISED_COLUMNS.map((col) => ({
    name: col.name,
    required: col.required,
    description: col.description,
  }));
}

/**
 * Map a single CSV data row (array of cell values) to a flight payload object
 * using the header mapping from parseCSVHeaders.
 *
 * @param {string[]} row - Array of cell values from a CSV line
 * @param {Object} headerMapping - { normalisedName → columnIndex } from parseCSVHeaders
 * @returns {Object} Flight payload object
 */
export function mapCSVRow(row, headerMapping) {
  const mapped = {};

  const get = (field) => {
    const idx = headerMapping[field];
    if (idx == null || idx >= row.length) return null;
    const val = row[idx];
    return val != null && String(val).trim() !== '' ? String(val).trim() : null;
  };

  // Required fields
  mapped.flight_date = get('flight_date') || null;
  mapped.aircraft = get('aircraft') || '';
  mapped.pilot = normalisePilotName(get('pilot'));

  // Block time — parse to minutes
  const blockRaw = get('block_time_mins');
  mapped.block_time_mins = blockRaw != null ? parseTimeToMins(blockRaw) : NaN;

  // Optional fields
  mapped.departure = get('departure') || '';
  mapped.destination = get('destination') || '';

  const flightTimeRaw = get('flight_time_mins');
  mapped.flight_time_mins = flightTimeRaw != null ? parseTimeToMins(flightTimeRaw) : NaN;

  // Fuel levels
  mapped.main_tank_level = mapFuelLevel(get('main_tank_level'));
  mapped.right_wing_level = mapFuelLevel(get('right_wing_level'));
  mapped.belly_tank_level = mapFuelLevel(get('belly_tank_level'));

  // Fuel uplift — parse "50L @ £1.80" pattern
  const fuelRaw = get('fuel_uplift');
  if (fuelRaw) {
    const litresMatch = fuelRaw.match(/([\d.]+)\s*[Ll]?/);
    const costMatch = fuelRaw.match(/[£$]([\d.]+)/);
    mapped.fuel_uplift_litres = litresMatch ? parseFloat(litresMatch[1]) : null;
    mapped.fuel_uplift_cost = costMatch ? parseFloat(costMatch[1]) : null;
  } else {
    mapped.fuel_uplift_litres = null;
    mapped.fuel_uplift_cost = null;
  }

  // Oil level
  const oilRaw = get('oil_level');
  mapped.oil_level = oilRaw != null ? (isNaN(parseFloat(oilRaw)) ? null : parseFloat(oilRaw)) : null;

  // Notes
  mapped.notes = get('notes') || '';

  return mapped;
}

// ── Internal CSV parsing helpers ────────────────────────────────────────────

/**
 * Split a CSV line into an array of cell values.
 * Handles quoted fields containing commas.
 *
 * @param {string} line - A single CSV line
 * @returns {string[]}
 */
function splitCSVLine(line) {
  const cells = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];

    if (inQuotes) {
      if (ch === '"') {
        // Check for escaped quote ("")
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"';
          i++; // skip next quote
        } else {
          inQuotes = false;
        }
      } else {
        current += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === ',') {
        cells.push(current);
        current = '';
      } else {
        current += ch;
      }
    }
  }

  cells.push(current);
  return cells;
}

/**
 * Validate a mapped CSV row. Returns { valid, errors }.
 * Same validation rules as validateExcelRow.
 */
function validateCSVRow(mapped) {
  const errors = [];

  if (mapped.flight_date == null || mapped.flight_date === '') {
    errors.push('Missing or empty flight_date');
  }

  if (!mapped.aircraft) {
    errors.push('Missing or empty aircraft name');
  }

  if (!mapped.pilot) {
    errors.push('Missing or empty pilot name');
  }

  if (isNaN(mapped.block_time_mins) || mapped.block_time_mins <= 0) {
    errors.push('block_time_mins must be a positive number');
  }

  return { valid: errors.length === 0, errors };
}

// ── Component ───────────────────────────────────────────────────────────────

/**
 * Creates the CSV import panel with header-driven field mapping.
 * @returns {HTMLElement}
 */
export function createCSVImport() {
  const section = document.createElement('section');
  section.className = 'settings-section csv-import';

  const heading = document.createElement('h2');
  heading.textContent = 'Import CSV';
  section.appendChild(heading);

  const description = document.createElement('p');
  description.className = 'csv-import__description';
  description.textContent =
    'Upload a CSV file with flight data. The first row is used as column headers to map fields. ' +
    'Rows that already exist in the database will be skipped automatically.';
  section.appendChild(description);

  // Column reference table
  const colRef = document.createElement('details');
  colRef.open = true;
  colRef.className = 'csv-import__column-ref';

  const colRefSummary = document.createElement('summary');
  colRefSummary.textContent = 'Expected Columns';
  colRef.appendChild(colRefSummary);

  const columns = getColumnReference();
  const grid = document.createElement('div');
  grid.className = 'column-ref-grid';

  // Split columns into two halves for a two-column layout
  const mid = Math.ceil(columns.length / 2);
  const halves = [columns.slice(0, mid), columns.slice(mid)];

  halves.forEach((half) => {
    const table = document.createElement('table');
    const thead = document.createElement('thead');
    const headerRow = document.createElement('tr');
    ['Column', 'Req', 'Format'].forEach((text) => {
      const th = document.createElement('th');
      th.textContent = text;
      headerRow.appendChild(th);
    });
    thead.appendChild(headerRow);
    table.appendChild(thead);

    const tbody = document.createElement('tbody');
    half.forEach((col) => {
      const tr = document.createElement('tr');

      const tdName = document.createElement('td');
      if (col.required) {
        const strong = document.createElement('strong');
        strong.textContent = `*${col.name}`;
        tdName.appendChild(strong);
      } else {
        tdName.textContent = col.name;
      }
      tr.appendChild(tdName);

      const tdReq = document.createElement('td');
      tdReq.textContent = col.required ? 'Yes' : '';
      tr.appendChild(tdReq);

      const tdFmt = document.createElement('td');
      tdFmt.textContent = col.description;
      tr.appendChild(tdFmt);

      tbody.appendChild(tr);
    });
    table.appendChild(tbody);
    grid.appendChild(table);
  });

  colRef.appendChild(grid);
  section.appendChild(colRef);

  // Message area
  const messageArea = document.createElement('div');
  messageArea.className = 'csv-import__message';
  section.appendChild(messageArea);

  // Progress area
  const progressArea = document.createElement('div');
  progressArea.className = 'csv-import__progress';
  progressArea.style.display = 'none';
  section.appendChild(progressArea);

  // Summary area
  const summaryArea = document.createElement('div');
  summaryArea.className = 'csv-import__summary';
  summaryArea.style.display = 'none';
  section.appendChild(summaryArea);

  // Buttons container
  const btnContainer = document.createElement('div');
  btnContainer.className = 'csv-import__buttons';

  // File input
  const fileInput = document.createElement('input');
  fileInput.type = 'file';
  fileInput.accept = '.csv';
  fileInput.className = 'csv-import__file-input';

  // Upload button
  const uploadBtn = document.createElement('button');
  uploadBtn.type = 'button';
  uploadBtn.className = 'btn btn--primary';
  uploadBtn.textContent = 'Import';
  uploadBtn.disabled = true;

  // Download template button
  const templateBtn = document.createElement('button');
  templateBtn.type = 'button';
  templateBtn.className = 'btn btn--secondary';
  templateBtn.textContent = 'Download Template';

  btnContainer.appendChild(fileInput);
  btnContainer.appendChild(uploadBtn);
  btnContainer.appendChild(templateBtn);
  section.appendChild(btnContainer);

  // ── Helpers ─────────────────────────────────────────────────────────────

  function showMessage(text, type = 'success') {
    messageArea.textContent = text;
    messageArea.className = `csv-import__message csv-import__message--${type}`;
  }

  function clearMessage() {
    messageArea.textContent = '';
    messageArea.className = 'csv-import__message';
  }

  function showProgress(text) {
    progressArea.textContent = text;
    progressArea.style.display = 'block';
  }

  function hideProgress() {
    progressArea.textContent = '';
    progressArea.style.display = 'none';
  }

  function setButtonsDisabled(disabled) {
    uploadBtn.disabled = disabled;
    templateBtn.disabled = disabled;
    fileInput.disabled = disabled;
  }

  function showSummary(imported, skipped, failed, errors) {
    summaryArea.innerHTML = '';
    summaryArea.style.display = 'block';

    const title = document.createElement('h3');
    title.textContent = 'Import Summary';
    summaryArea.appendChild(title);

    const counts = document.createElement('div');
    counts.className = 'csv-import__counts';

    const items = [
      { label: 'Imported', value: imported, cls: 'success' },
      { label: 'Skipped (duplicates)', value: skipped, cls: 'info' },
      { label: 'Failed', value: failed, cls: failed > 0 ? 'error' : 'success' },
    ];

    items.forEach(({ label, value, cls }) => {
      const item = document.createElement('span');
      item.className = `csv-import__count csv-import__count--${cls}`;
      item.textContent = `${label}: ${value}`;
      counts.appendChild(item);
    });

    summaryArea.appendChild(counts);

    if (errors.length > 0) {
      const errorList = document.createElement('details');
      const errorSummary = document.createElement('summary');
      errorSummary.textContent = `${errors.length} error(s)`;
      errorList.appendChild(errorSummary);

      const ul = document.createElement('ul');
      ul.className = 'csv-import__errors';
      errors.forEach((err) => {
        const li = document.createElement('li');
        li.textContent = err;
        ul.appendChild(li);
      });
      errorList.appendChild(ul);
      summaryArea.appendChild(errorList);
    }
  }

  // ── File selection ──────────────────────────────────────────────────────

  fileInput.addEventListener('change', () => {
    uploadBtn.disabled = !fileInput.files || fileInput.files.length === 0;
    clearMessage();
    summaryArea.style.display = 'none';
  });

  // ── Template download ───────────────────────────────────────────────────

  templateBtn.addEventListener('click', () => {
    try {
      const { headerLine, exampleLine } = buildCSVTemplate();
      const csvContent = headerLine + '\n' + exampleLine + '\n';

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'flight-import-template.csv';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      showMessage(`Template download failed: ${err.message}`, 'error');
    }
  });

  // ── Import ──────────────────────────────────────────────────────────────

  uploadBtn.addEventListener('click', async () => {
    const file = fileInput.files[0];
    if (!file) return;

    setButtonsDisabled(true);
    clearMessage();
    summaryArea.style.display = 'none';
    showProgress('Reading file…');

    try {
      await performImport(file);
    } catch (err) {
      showMessage(err.message || 'Import failed', 'error');
    } finally {
      hideProgress();
      setButtonsDisabled(false);
      uploadBtn.disabled = true;
    }
  });

  async function performImport(file) {
    // 1. Read the CSV file as text
    let text;
    try {
      text = await file.text();
    } catch (err) {
      throw new Error('Unable to parse file — ensure it is a valid CSV file');
    }

    // Split into lines, removing trailing empty lines
    const lines = text.split(/\r?\n/).filter((line, idx, arr) => {
      // Keep all lines except trailing empty ones
      if (line.trim() === '' && idx === arr.length - 1) return false;
      return true;
    });

    if (lines.length === 0) {
      throw new Error('Unable to parse file — ensure it is a valid CSV file');
    }

    // 2. Parse and validate header row
    showProgress('Validating headers…');
    const headerLine = lines[0];
    const { mappedFields, missingRequired } = parseCSVHeaders(headerLine);

    if (missingRequired.length > 0) {
      throw new Error(`Missing required columns: ${missingRequired.join(', ')}`);
    }

    if (lines.length < 2) {
      showSummary(0, 0, 0, []);
      showMessage('No data rows found in the uploaded file.', 'error');
      return;
    }

    // 3. Map and validate data rows
    showProgress('Processing rows…');
    const validRows = [];
    const errors = [];
    let failCount = 0;

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      if (line.trim() === '') continue; // skip blank lines

      const rowNum = i + 1; // 1-based, accounting for header
      const cells = splitCSVLine(line);
      const mapped = mapCSVRow(cells, mappedFields);
      const result = validateCSVRow(mapped);

      if (!result.valid) {
        errors.push(`Row ${rowNum}: ${result.errors.join('; ')}`);
        failCount++;
      } else {
        validRows.push({ ...mapped, _rowNum: rowNum });
      }
    }

    if (validRows.length === 0 && failCount > 0) {
      showSummary(0, 0, failCount, errors);
      showMessage('No valid rows to import', 'error');
      return;
    }

    if (validRows.length === 0) {
      showSummary(0, 0, 0, []);
      showMessage('No data rows found in the uploaded file.', 'error');
      return;
    }

    // 4. Resolve aircraft_id and pilot_id via API lookups
    showProgress('Resolving aircraft and pilots…');
    const [allAircraft, allPilots] = await Promise.all([getAircraft(), getPilots()]);

    // Build lookup maps (case-insensitive)
    const aircraftMap = new Map();
    for (const ac of allAircraft) {
      if (ac.name) aircraftMap.set(ac.name.toLowerCase(), ac.id);
      if (ac.registration) aircraftMap.set(ac.registration.toLowerCase(), ac.id);
    }

    const pilotMap = new Map();
    for (const p of allPilots) {
      // Resolve aliases to parent UUID for import (Requirement 8.13)
      if (p.name) pilotMap.set(p.name.toLowerCase(), resolveImportPilotId(p));
    }

    // Resolve IDs for each valid row
    const resolvedRows = [];
    for (const row of validRows) {
      const aircraftKey = (row.aircraft || '').toLowerCase();
      const pilotKey = normalisePilotName(row.pilot).toLowerCase();

      const aircraft_id = aircraftMap.get(aircraftKey);
      const pilot_id = pilotMap.get(pilotKey);

      if (!aircraft_id) {
        errors.push(`Row ${row._rowNum}: Aircraft "${row.aircraft}" not found`);
        failCount++;
        continue;
      }

      if (!pilot_id) {
        errors.push(`Row ${row._rowNum}: Pilot "${row.pilot}" not found`);
        failCount++;
        continue;
      }

      resolvedRows.push({ ...row, aircraft_id, pilot_id });
    }

    if (resolvedRows.length === 0) {
      showSummary(0, 0, failCount, errors);
      showMessage('No rows could be resolved to existing aircraft/pilots.', 'error');
      return;
    }

    // 5. Dedup check
    showProgress('Checking for duplicates…');
    let existingKeys;
    try {
      existingKeys = await findExistingFlights(resolvedRows);
    } catch (err) {
      throw new Error(`Dedup check failed: ${err.message}`);
    }

    // Partition into new vs duplicate rows
    const newRows = [];
    let skippedCount = 0;

    for (const row of resolvedRows) {
      const key = buildDedupKey(row);
      if (existingKeys.has(key)) {
        skippedCount++;
      } else {
        newRows.push(row);
      }
    }

    if (newRows.length === 0) {
      showSummary(0, skippedCount, failCount, errors);
      showMessage('All rows already exist in the database — nothing to import.', 'info');
      return;
    }

    // 6. Insert new rows
    showProgress(`Importing ${newRows.length} flights…`);
    let importedCount = 0;

    for (const row of newRows) {
      try {
        const flightPayload = {
          flight_date: row.flight_date,
          aircraft_id: row.aircraft_id,
          pilot_id: row.pilot_id,
          departure: row.departure || null,
          destination: row.destination || null,
          block_time_mins: row.block_time_mins,
          flight_time_mins: isNaN(row.flight_time_mins) ? null : row.flight_time_mins,
          notes: row.notes || null,
          captain_name: row.pilot,
          status: 'FLOWN',
          source: 'CSV_UI',
          log_complete: true,
          start_time: '09:00',
          end_time: (() => {
            const blockMins = row.block_time_mins || 60;
            const endMins = 9 * 60 + blockMins;
            const h = Math.min(Math.floor(endMins / 60), 23);
            const m = endMins % 60;
            return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
          })(),
        };

        const flight = await createFlight(flightPayload);

        // Insert fuel/oil record if any fuel data is present
        if (
          row.main_tank_level ||
          row.right_wing_level ||
          row.belly_tank_level ||
          row.fuel_uplift_litres ||
          row.oil_level != null
        ) {
          await createFuelOil({
            flight_id: flight.id,
            main_tank_level: row.main_tank_level || null,
            right_wing_level: row.right_wing_level || null,
            belly_tank_level: row.belly_tank_level || null,
            fuel_uplift_litres: row.fuel_uplift_litres || null,
            fuel_uplift_cost: row.fuel_uplift_cost || null,
            oil_level: row.oil_level || null,
          });
        }

        importedCount++;
      } catch (err) {
        errors.push(`Row ${row._rowNum}: ${err.message}`);
        failCount++;
      }
    }

    // 7. Display summary
    showSummary(importedCount, skippedCount, failCount, errors);

    if (importedCount > 0) {
      showMessage(`Successfully imported ${importedCount} flight(s).`, 'success');
    } else if (failCount > 0) {
      showMessage('Import completed with errors — see summary below.', 'error');
    }
  }

  return section;
}
